<template>
  <form @submit.prevent="submitForm">
    <slot name="additional-before"></slot>
    <app-flex-grid v-bind="flexGridProps">
      <template v-for="(field, index) in formConfig" v-slot:[`col-${field.grid.row}-${field.grid.col}`]>
        <app-form-group
          v-if="!field.isHidden"
          :key="index"
          class="form-group--no-indent"
          :label="field.label"
          :label-for="field.attrs?.id || field.model"
          :label_left="field.labelLeft || false"
          :disabled="field.disabled"
          :required="isFieldRequired(field)"
        >
          <template #default>
            <component
              :is="field.component"
              v-model="form[field.model]"
              v-bind="field.attrs || {}"
              :id="field.attrs?.id || field.model"
              v-on="field.events || {}"
              :error="$v.form[field.model]?.$error"
            />
          </template>
          <template #error>
            <div v-if="$v.form[field.model]?.$error && $v.form[field.model].$dirty">
              <div v-for="(message, index) in validationMessages(field)" :key="index">
                {{ message }}
              </div>
            </div>
          </template>
        </app-form-group>
      </template>
      <template v-for="(row, index) in computedFlexGridRows" v-slot:[`header-${index+1}`]>
        <slot :name="`header-${index+1}`"></slot>
      </template>
    </app-flex-grid>
    <slot name="additional-after"></slot>
    <app-cells :position="buttonSubmitPosition" :indent="false">
      <app-button :disabled="$v.form.$error" type="submit" ref="submit_button">
        {{ buttonSubmitText }}
      </app-button>
    </app-cells>
  </form>
</template>

<script>
export default {
  props: {
    formConfig: {
      type: Array,
      required: true,
      validator: value => {
        const logError = (index, property) => {
          console.error(`Field at index ${index} is missing a valid '${property}' property.`)
        }
        return value.every((field, index) => {
          const hasComponent = field.component && typeof field.component === 'string'
          const hasModel = field.model && typeof field.model === 'string'
          const hasGrid = field.grid && typeof field.grid.row !== 'undefined' && typeof field.grid.col !== 'undefined' && typeof field.grid.width !== 'undefined'
          if (!hasComponent) logError(index, 'component')
          if (!hasModel) logError(index, 'model')
          if (!hasGrid) logError(index, 'grid')

          return hasComponent && hasModel && hasGrid
        })
      }
    },
    form: {
      type: Object,
      required: true
    },
    gapRows: {
      type: [String, Number],
      default: '20px'
    },
    gapCols: {
      type: [String, Number],
      default: '20px'
    },
    marginTop: {
      type: [String, Number],
      default: '0px'
    },
    marginBottom: {
      type: [String, Number],
      default: '20px'
    },
    alignItems: {
      type: String,
      default: 'center'
    },
    buttonSubmitText: {
      type: String,
      default: 'Create'
    },
    buttonSubmitPosition: {
      type: String,
      default: 'center'
    },
  },
  computed: {
    flexGridProps() {
      return {
        rows: this.computedFlexGridRows,
        gapRows: this.gapRows,
        gapCols: this.gapCols,
        marginTop: this.marginTop,
        marginBottom: this.marginBottom,
        alignItems: this.alignItems
      }
    },
    computedFlexGridRows() {
      const rowMap = {}
      this.formConfig.forEach(field => {
        const { row, col, width } = field.grid
        if (!rowMap[row - 1]) {
          rowMap[row - 1] = []
        }
        rowMap[row - 1][col - 1] = width || 1
      })
      return Object.values(rowMap)
    }
  },
  methods: {
    isFieldRequired(field) {
      return field.validations && field.validations.required !== null
    },
    submitForm() {
      this.$v.form.$touch()
      if (!this.$v.form.$invalid) {
        this.$emit('submit', this.form)
      } else {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly'
        })
      }
    },
    validationMessages(field) {
      if (!field.validations || !this.$v.form || !this.$v.form[field.model]) {
        return []
      }
      const messages = field.validationMessages || {}
      return Object.keys(field.validations).filter(key => {
        return !this.$v.form[field.model][key]
      }).map(key => {
        if (key === 'required' && !messages[key]) {
          return 'Required field'
        }
        return messages[key]
      })
    }
  },
  validations() {
    const validations = {}
    this.formConfig.forEach(field => {
      if (field.validations) {
        validations[field.model] = field.validations
      }
    })
    return { form: validations }
  }
}
</script>